import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { Link, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { DetailsProps, DetailsRow } from "./types";
import React, { useMemo } from "react";
import {
  defaultRenderArray,
  defaultRenderDates,
  defaultRenderObject,
} from "./utils";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

function Details(props: DetailsProps) {
  /** In case we want to render arrays as columns  */
  const calculateMaxNumberOfColumns = () => {
    const rows = props?.rows as any;
    if (!rows || !Array.isArray(rows)) return 0;
    const maxNumberOfColumns = rows.reduce((max: number, row: any) => {
      return Math.max(max, row?.value?.length);
    }, 0);
    return maxNumberOfColumns;
  };

  const location = useLocation();
  const { t } = useTranslation();

  const {
    rows = [],
    title,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    style, // this is unused
    renderArraysAsColumns = false,
    renderAsStripped = false,
    renderFirstRowsAsHeader = false,
    removeLines = false,
    renderDates = defaultRenderDates,
    renderArrays = defaultRenderArray,
    renderObject = defaultRenderObject,
  } = props;

  const maxNumberOfColumns = useMemo(
    () => calculateMaxNumberOfColumns(),
    [renderArraysAsColumns, rows.length],
  );

  const renderValue = (value: any) =>
    value instanceof Date
      ? renderDates(value)
      : Array.isArray(value)
        ? renderArrays(value)
        : typeof value === "object" && value !== null && "url" in value
          ? value.label
          : React.isValidElement(value)
            ? value
            : typeof value === "object" && value !== null
              ? renderObject(value)
              : value;

  const renderCell = (
    value: any,
    renderFirstRowsAsHeader: boolean,
    index: number,
    renderBold?: boolean,
    isHeader?: boolean,
    // eslint-disable-next-line max-params
  ) => {
    return (
      <TableCell
        variant={index == 0 || isHeader ? "head" : "body"}
        scope="row"
        align={isHeader ? "center" : "left"}
        key={value}
        colSpan={isHeader ? 2 : 1}
        sx={{ borderBottom: isHeader ? "none" : null }}
      >
        {typeof value === "object" && value !== null && "url" in value ? (
          "externalLink" in value ? (
            <a href={renderValue(value.url)} target="_blank" rel="noreferrer">
              {renderValue(value)}
            </a>
          ) : (
            <Link
              fontWeight={
                renderFirstRowsAsHeader && index === 0 ? "650" : "500"
              }
            >
              {renderValue(value)}
            </Link>
          )
        ) : (
          <Typography
            fontWeight={
              renderFirstRowsAsHeader
                ? index === 0
                  ? "650"
                  : "500"
                : renderBold
                  ? "bold"
                  : "regular"
            }
          >
            {renderValue(value)}
          </Typography>
        )}
      </TableCell>
    );
  };

  const renderEmptyCells = (numberOfCells: number) =>
    [...Array.from(Array(numberOfCells).keys())].map((x) => (
      <TableCell align="left" key={x}>
        <Typography></Typography>
      </TableCell>
    ));

  /** Render cells adding as many cells as the max number of columns */
  const renderValuesAsColumns = (row: any, indexRow: number) =>
    row.value.map((value: any, index: number) => {
      return index < row.value.length
        ? renderCell(value, renderFirstRowsAsHeader, indexRow)
        : renderEmptyCells(maxNumberOfColumns - row.value.length);
    });

  const renderRow = (row: DetailsRow, index: number) => {
    return (
      <>
        <TableRow
          key={`row-${index}`}
          sx={{
            backgroundColor:
              renderAsStripped || row.name === "header"
                ? // ? index % 2 === 0
                  "#eeeeee"
                : // : ''
                  "",
          }}
        >
          {row.name !== "header" &&
            row.name !== "objectArray" &&
            renderCell(row.name, renderFirstRowsAsHeader, index, true)}
          {renderArraysAsColumns && Array.isArray(row.value)
            ? renderValuesAsColumns(row, index)
            : renderCell(
                row.value,
                renderFirstRowsAsHeader,
                index,
                row.name === "header",
                row.name === "header" || row.name === "objectArray",
              )}
        </TableRow>
      </>
    );
  };

  const renderRows = (rows: DetailsRow[]) => {
    return rows.map((row, index) => {
      return row.isHiddenRow ? null : renderRow(row, index + 1);
    });
  };

  return (
    <div>
      <Box
        display="flex"
        gap="10px"
        // flexWrap='wrap'
        flexDirection="column"
      >
        <Typography variant="h4" style={{ marginLeft: "10px" }}>
          {title}
        </Typography>
        <TableContainer sx={{ overflow: "auto" }} id="table-details">
          <Table
            size="small"
            aria-label="simple table"
            sx={{
              tableLayout: "fixed",
              [`& .${tableCellClasses.root}`]: {
                borderBottom: removeLines && "none",
              },
            }}
          >
            <TableBody>{renderRows(rows as any)}</TableBody>
          </Table>
        </TableContainer>
      </Box>
    </div>
  );
}

export default Details;
